/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import { findSeriesDataItemInfo, getDataItemValue } from '../../helper/standardChart';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 x 轴指标数据值 */
  xValue: string;
  /** 要标记的 y 轴指标数据值 */
  yValue?: string;
  /** 文本描述 */
  summary: string;
  /** 文本样式 @see https://echarts.apache.org/zh/option.html#series-line.markPoint.data.label */
  textStyle?: unknown;
};

const type = 'Text';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markPoint = series.markPoint || {};
        series.markPoint.data = (series.markPoint.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
    }
  },
  render: {
    onOptionUpdate(context, option) {
      const insight = context.insightOption;

      function render(data: InsightData) {
        const { x, y, xValue, yValue, summary, textStyle = {} } = data;
        const dataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue,
          yValue
        });

        if (!dataItemMetaInfo) {
          return;
        }
        const { seriesIndex, dataItemIndex } = dataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        const [xVal, yVal] = [xValue, getDataItemValue(series.data[dataItemIndex])];
        // 适配散点图（yVal：[x,y]包含了坐标）
        const isArray = Array.isArray(yVal);

        series.markPoint = series.markPoint || {};
        series.markPoint.data = series.markPoint.data || [];

        const text = {
          symbolSize: 0,
          label: {
            ...context.util.data.deepMerge(textStyle, {
              show: true,
              position: 'top',
              formatter: summary
            })
          },
          coord: isArray ? yVal : [xVal, yVal]
        };
        (text as unknown as { __insightFlag: string }).__insightFlag = INSIGHT_FLAG;

        series.markPoint.data.push(text);
      }

      insight.data.forEach(item => render(item));
    }
  }
});
